import React, { useRef } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { ILeftFeedProps, LeftFeedLayer } from './LeftFeedLayer';
import { IRightFeedLayer, RightFeedLayer } from './RightFeedLayer';
import { useScrollEndDetector } from '../../hook/useScrollEndDetector';

export interface ISplitScrollProps extends ILeftFeedProps, IRightFeedLayer {
    onLeftFeedScrollEndReached?: () => void;
    onRightFeedScrollEndReached?: () => void;
}

const SplitScroll: React.FC<ISplitScrollProps> = ({
    onLeftFeedScrollEndReached,
    onRightFeedScrollEndReached,
    ...props
}) => {
    const muiTheme = useTheme();

    const scrollRefRight = useRef<HTMLDivElement>(null);
    const scrollRefLeft = useRef<HTMLDivElement>(null);
    const offset = 2000;
    useScrollEndDetector(scrollRefLeft, onLeftFeedScrollEndReached, offset);
    useScrollEndDetector(scrollRefRight, onRightFeedScrollEndReached, offset);

    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'row',
                height: '100vh',
                width: '100%',
                maxWidth: '100vw',
                position: 'relative',
            }}>
            {/* Left Side */}
            <Box
                ref={scrollRefLeft}
                sx={{
                    flex: {
                        xs: 1,
                        md: 0.6,
                    },
                    overflowX: 'hidden',
                    overflowY: 'auto',
                    width: {
                        xs: '100%',
                        md: '60%',
                    },
                    maxWidth: {
                        xs: '100%',
                        md: '800px',
                    },
                    WebkitOverflowScrolling: 'touch',
                    scrollBehavior: 'smooth',
                    overscrollBehavior: 'contain',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': {
                        display: 'none',
                    },
                    px: {
                        xs: 0,
                        sm: 2,
                    },
                    pb: 2,
                    height: '100vh',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    textAlign: 'center',
                }}>
                <LeftFeedLayer {...props} />
            </Box>

            {/* Right Side */}
            {!isMobile && (
                <Box
                    ref={scrollRefRight}
                    sx={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        width: '40%',
                        height: '100vh',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        py: 2,
                        px: 1,
                        pr: 2,
                        pb: 20, // Add extra bottom padding
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'flex-start',
                        textAlign: 'left',
                        WebkitOverflowScrolling: 'touch',
                        scrollBehavior: 'smooth',
                        overscrollBehavior: 'contain',
                        scrollbarWidth: 'none',
                        '&::-webkit-scrollbar': {
                            display: 'none',
                        },
                    }}>
                    <RightFeedLayer {...props} />
                </Box>
            )}
        </Box>
    );
};

export default SplitScroll;
